/**
 * Fecha y hora, en un solo lugar y en 24 horas.
 *
 * El 2026-09-02, recorriendo el producto como usuario final, un caso creado a las 15:55
 * aparecia en la ficha como `2/9/2026, 03:55:17`. Dos veces en la misma pantalla, y sin
 * AM/PM: las 15:55 y las 03:55 se imprimian identicas.
 *
 * La causa es contraintuitiva, y por eso conviene dejarla escrita: **el bug lo produce pasar
 * el locale.** En el ICU actual `es-AR` resuelve `hourCycle: h12`, y `toLocaleString` sin
 * opciones usa un formato corto que descarta el marcador de periodo. O sea:
 *
 *     toLocaleString('es-AR')                 -> "2/9/2026, 03:55:17"   <- 12h, sin AM/PM
 *     toLocaleString('es-AR', {hour12:false}) -> "2/9/2026, 15:55:17"
 *     toLocaleString()                        -> "2/9/2026, 15:55:17"
 *
 * Quien escribio `'es-AR'` lo hizo para que la fecha saliera en formato argentino. Salio, y
 * de paso se llevo la hora.
 *
 * En un producto donde el reloj de SLA es el argumento, una hora con dos lecturas posibles a
 * doce horas de distancia no es un detalle de formato: convierte un caso en plazo en uno
 * vencido, y no se ve como un error — se ve como un numero, y el numero es creible.
 *
 * Todo lo que muestre una hora pasa por aca. Hay un test que lo fija
 * (`test_la_hora_se_lee_de_una_sola_forma.py`), porque esto se corrige en un lugar y
 * reaparece en otro: ya habia sitios con `hour12:false` explicito conviviendo con los rotos.
 */

const LOCALE = 'es-AR';

//: `hourCycle: 'h23'` y no `hour12: false`. Los dos dan 24 horas, pero `hour12:false` con
//: algunos locales cae en `h24` (medianoche = 24:00 en vez de 00:00). `h23` es el que dice
//: exactamente lo que queremos.
const HORA = { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' };
const HORA_CON_SEGUNDOS = { ...HORA, second: '2-digit' };
const FECHA = { day: '2-digit', month: '2-digit', year: 'numeric' };
const FECHA_CORTA = { day: '2-digit', month: '2-digit' };

//: Lo que se muestra cuando no hay fecha. Un guion y no una cadena vacia: un hueco visible se
//: lee como "no hay dato"; uno invisible se lee como que la pantalla no termino de cargar.
export const SIN_FECHA = '—';

function _fecha(valor) {
  if (valor === null || valor === undefined || valor === '') return null;
  const d = valor instanceof Date ? valor : new Date(valor);
  // Un `Invalid Date` formateado imprime "Invalid Date" adentro de la pantalla. Se corta aca.
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Fecha y hora: `02/09/2026, 15:55`. Es el formato por omision del producto. */
export function fechaYHora(valor, vacio = SIN_FECHA) {
  const d = _fecha(valor);
  if (!d) return vacio;
  return `${d.toLocaleDateString(LOCALE, FECHA)}, ${d.toLocaleTimeString(LOCALE, HORA)}`;
}

/** Con segundos: `02/09/2026, 15:55:17`. Para logs y autopsia, donde el segundo importa. */
export function fechaYHoraConSegundos(valor, vacio = SIN_FECHA) {
  const d = _fecha(valor);
  if (!d) return vacio;
  return `${d.toLocaleDateString(LOCALE, FECHA)}, ${d.toLocaleTimeString(LOCALE, HORA_CON_SEGUNDOS)}`;
}

/** Solo la fecha: `02/09/2026`. */
export function soloFecha(valor, vacio = SIN_FECHA) {
  const d = _fecha(valor);
  return d ? d.toLocaleDateString(LOCALE, FECHA) : vacio;
}

/** Dia y mes, sin año: `02/09`. Para columnas angostas donde el año no aporta. */
export function fechaCorta(valor, vacio = SIN_FECHA) {
  const d = _fecha(valor);
  return d ? d.toLocaleDateString(LOCALE, FECHA_CORTA) : vacio;
}

/** Solo la hora: `15:55`. */
export function soloHora(valor, vacio = SIN_FECHA) {
  const d = _fecha(valor);
  return d ? d.toLocaleTimeString(LOCALE, HORA) : vacio;
}

/** Con segundos, sin fecha: `15:55:17`. */
export function soloHoraConSegundos(valor, vacio = SIN_FECHA) {
  const d = _fecha(valor);
  return d ? d.toLocaleTimeString(LOCALE, HORA_CON_SEGUNDOS) : vacio;
}
